import type { PaginatedResponse } from './responses';

export interface OrganizationSettings {
    id: string;
    name: string;
    logoUrl: string;
}

export interface Member {
    id: string;
    grantId: string;
    email: string;
    name: string;
    roles: string[];
    lastSeen: string | null;
    isOrgOwner: boolean;
    isOrgUserManager: boolean;
}

export type MembersPaginatedResponse = PaginatedResponse<Member>;

export interface Seats {
    current: number;
    max: number;
}

export enum LimitSource {
    Override = 'override',
    Default = 'default',
    License = 'license',
}

export interface EffectiveLimitField<T = number> {
    value: T;
    source: LimitSource;
}

export interface OrganizationConfiguration {
    maxUsers: EffectiveLimitField;
    maxCameras: EffectiveLimitField;
    hdmiEnabled: EffectiveLimitField<boolean>;
    videoFilesEnabled: EffectiveLimitField<boolean>;
    liveCamerasEnabled: EffectiveLimitField<boolean>;
    alertsEnabled: EffectiveLimitField<boolean>;
    currentUsers: number;
    currentCameras: number;
    currentAlertUnits: number;
    maxAlertUnits: number | null;
    dataRetentionDays: number;
}

export interface OrganizationFeatureFlags {
    hdmiEnabled: boolean;
    videoFilesEnabled: boolean;
    liveCamerasEnabled: boolean;
    alertsEnabled: boolean;
}

export interface UpdateOrganizationConfigurationRequest {
    maxUsers?: number;
    maxCameras?: number;
    hdmiEnabled?: boolean;
    videoFilesEnabled?: boolean;
    liveCamerasEnabled?: boolean;
    alertsEnabled?: boolean;
    maxAlertUnits?: number | null;
}

export type AuditEventsPaginatedResponse = PaginatedResponse<AuditEvent>;

export interface CreateMemberPayload {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    password: string;
    isOrgOwner: boolean;
    isOrgUserManager: boolean;
}

export interface PasswordComplexityPolicy {
    minLength: number;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSymbol: boolean;
}

export type AuditEventSource = 'zitadel' | 'platform';

export interface AuditEvent {
    id: string;
    source: AuditEventSource;
    type: string;
    createdAt: string;
    userId: string | null;
    userEmail: string | null;
    userName: string | null;
    details: Record<string, unknown>;
}

export interface AuditEventsParams {
    page?: number;
    size?: number;
    eventTypes?: string[];
    startDate?: string;
    endDate?: string;
    userIds?: string[];
}

export interface MembersParams {
    page?: number;
    size?: number;
    name?: string;
}

export interface UpdateMemberRolesPayload {
    roles: string[];
}

export interface RetentionSettings {
    dataRetentionDays: number;
    cleanupCron: string;
}

export interface MetricWithDelta {
    value: number;
    previous: number;
    deltaPercent: number | null;
}

export interface ActivityPulse {
    windowDays: number;
    activeUsers: MetricWithDelta;
    sourcesAdded: MetricWithDelta;
    alertVolume: MetricWithDelta;
}

export type ActivityPulseWindow = 7 | 30;

export interface TrendPoint {
    date: string;
    value: number;
}

export interface ActivityTrends {
    windowDays: number;
    activeUsers: TrendPoint[];
    alertVolume: TrendPoint[];
}

export interface IntegrationStatus {
    key: string;
    name: string;
    configured: boolean;
}

export interface IntegrationsResponse {
    integrations: IntegrationStatus[];
}

export enum MemberState {
    ACTIVE = 'USER_STATE_ACTIVE',
    INACTIVE = 'USER_STATE_INACTIVE',
    LOCKED = 'USER_STATE_LOCKED',
    INITIAL = 'USER_STATE_INITIAL',
    UNSPECIFIED = 'USER_STATE_UNSPECIFIED',
    DELETED = 'USER_STATE_DELETED',
}

export interface MemberDetail {
    id: string;
    grantId: string;
    email: string;
    name: string;
    roles: string[];
    state: MemberState;
    lastLogin: string | null;
    invitedByEmail: string | null;
    recentEvents: AuditEvent[];
    isOrgOwner: boolean;
    isOrgUserManager: boolean;
}
